import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { getLocalDateString } from '../lib/attendanceUtils';
import { resolveMinuteMode } from '../lib/attendanceCalculation';
import type { AttendanceCalculationRule } from '../lib/attendanceCalculation';
import type { AdminEmployee } from '../types/models';

interface MinuteEmployeeSettingsProps {
  employees: AdminEmployee[];
  rules: AttendanceCalculationRule[];
  loading: boolean;
  onSave: (selectedIds: string[], effectiveFrom: string) => Promise<void>;
}

const normalizeSearch = (value: string): string => value.normalize('NFKC').replace(/\s+/g, '').toLowerCase();

const isValidDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const MinuteEmployeeSettings = ({ employees, rules, loading, onSave }: MinuteEmployeeSettingsProps) => {
  const [effectiveFrom, setEffectiveFrom] = useState(getLocalDateString);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [baselineIds, setBaselineIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const lastSavedKeyRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Stable content keys prevent unrelated parent renders from discarding the user's selection.
  const employeeIdsKey = JSON.stringify(employees.map(employee => employee.id).sort());
  const rulesKey = JSON.stringify(rules.map(rule => [rule.employeeId, rule.effectiveFrom, rule.mode]).sort());
  useEffect(() => {
    const employeeIds = JSON.parse(employeeIdsKey) as string[];
    const sourceRules = (JSON.parse(rulesKey) as [string, string, 'minute' | 'default'][]).map(
      ([employeeId, date, mode]) => ({ employeeId, effectiveFrom: date, mode })
    );
    const next = employeeIds.filter(id => resolveMinuteMode(sourceRules, id, effectiveFrom));
    setSelectedIds(next);
    setBaselineIds(next);
    setError(null);
    if (lastSavedKeyRef.current !== JSON.stringify([effectiveFrom, [...next].sort()])) setSuccess(null);
  }, [employeeIdsKey, rulesKey, effectiveFrom]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedEmployees = useMemo(
    () => employees.filter(employee => selectedSet.has(employee.id)),
    [employees, selectedSet]
  );
  const visibleEmployees = useMemo(() => {
    const keyword = normalizeSearch(search);
    return employees.filter(employee => !keyword || normalizeSearch(
      `${employee.name} ${employee.employeeCode ?? ''}`
    ).includes(keyword));
  }, [employees, search]);
  const isDirty = selectedIds.length !== baselineIds.length || baselineIds.some(id => !selectedSet.has(id));
  const disabled = loading || saving;

  const toggleEmployee = (id: string): void => {
    setSelectedIds(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async (): Promise<void> => {
    if (savingRef.current || loading || !isDirty || !isValidDate(effectiveFrom)) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const nextIds = [...selectedIds];
    try {
      await onSave(nextIds, effectiveFrom);
      setBaselineIds(nextIds);
      lastSavedKeyRef.current = JSON.stringify([effectiveFrom, [...nextIds].sort()]);
      setSuccess(`${effectiveFrom}からの対象者を保存しました。`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '対象者を保存できませんでした。もう一度お試しください。');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="minute-employee-settings-title" className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
      <div>
        <h3 id="minute-employee-settings-title" className="text-base font-semibold text-gray-900">1分単位で管理する従業員</h3>
        <p className="mt-1 text-sm text-gray-600">選択した従業員だけ、出勤時刻を切り上げずに集計します。選択していない従業員には通常の出勤時間設定を適用します。</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <label className="block text-sm font-medium text-gray-700">
          適用開始日
          <input
            type="date"
            value={effectiveFrom}
            disabled={disabled}
            onChange={event => setEffectiveFrom(event.target.value)}
            className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
          />
        </label>
        <p className="pb-2 text-sm text-gray-600">指定した日付時点の対象者を表示しています。</p>
      </div>
      <p className="text-xs text-gray-500">追加・解除は適用開始日以降の勤務に反映します。打刻した元の時刻は保持します。</p>

      <div className="rounded-lg bg-blue-50 p-3">
        <p className="text-sm font-medium text-blue-900">選択中：{selectedEmployees.length}人</p>
        {selectedEmployees.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-2" aria-label="選択中の従業員">
            {selectedEmployees.map(employee => (
              <li key={employee.id} className="flex items-center gap-1 rounded-full border border-blue-200 bg-white py-1 pl-3 pr-1 text-sm text-blue-900">
                <span>{employee.name}{employee.employeeCode ? `（${employee.employeeCode}）` : ''}</span>
                <button
                  type="button"
                  aria-label={`${employee.name}を対象から解除`}
                  disabled={disabled}
                  onClick={() => toggleEmployee(employee.id)}
                  className="rounded-full p-1 hover:bg-blue-100 disabled:opacity-50"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="mt-1 text-sm text-gray-600">対象者は選択されていません。</p>}
      </div>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">従業員を検索</span>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            disabled={disabled}
            placeholder="氏名・従業員コード"
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
          />
        </div>
      </label>

      <fieldset disabled={disabled} className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
        <legend className="sr-only">1分単位の対象者を選択</legend>
        {loading ? (
          <p className="flex items-center gap-2 p-4 text-sm text-gray-500"><Loader2 className="animate-spin" size={16} aria-hidden="true" />対象者を読み込み中...</p>
        ) : visibleEmployees.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">{employees.length === 0 ? '従業員が登録されていません。' : '検索条件に一致する従業員はいません。'}</p>
        ) : visibleEmployees.map(employee => (
          <label key={employee.id} className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={selectedSet.has(employee.id)}
              onChange={() => toggleEmployee(employee.id)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <span className="flex-1 text-sm text-gray-900">{employee.name} <span className="text-gray-500">{employee.employeeCode ?? 'コード未設定'}</span></span>
            {employee.status === 'retired' && <span className="text-xs text-gray-500">退職済み</span>}
          </label>
        ))}
      </fieldset>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {success && <p role="status" className="text-sm text-green-700">{success}</p>}
      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || !isDirty || !isValidDate(effectiveFrom)}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
      >
        {saving && <Loader2 className="animate-spin" size={16} aria-hidden="true" />}
        {saving ? '保存中...' : '対象者を保存'}
      </button>
    </section>
  );
};

export default MinuteEmployeeSettings;
